/**
 * What a stored file column actually points at.
 *
 * `talents.cv_url` (and `photo_url`) hold a storage **key** — `<uid>/cv/<file>`,
 * `talents/<row id>/cv/<file>`, `cv-imports/<…>` — never a URL. Put into an
 * `href`, a key renders as a relative path and 404s; that is what the
 * applicants panel and the candidate cover did with every CV, unnoticed while
 * almost nobody had one (drizzle/0045 is the same lesson, learnt on photos).
 *
 * The one exception is history: before `cv_url` became a storage property it
 * was a plain text field holding a link somebody pasted, and a row written
 * then may still carry `https://…`. That is a link and opens as one.
 *
 * No imports, so it can be tested on its own
 * (backend/src/services/storage-key-links.test.ts). Opening either kind is
 * `usePrivateFileOpener` in `_private_file.tsx`.
 */

export type FileTarget =
    /** A storage key, to be exchanged for a short-lived signed URL. */
    | { kind: "key"; key: string }
    /** A link somebody stored before the column held keys. */
    | { kind: "link"; href: string };

/** Schemes the storage layer itself writes in front of a key, and strips again. */
const STORAGE_SCHEMES = new Set(["local:", "s3:", "gs:"]);

export function fileTarget(value: unknown): FileTarget | null {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
        let url: URL;
        try {
            url = new URL(trimmed);
        } catch {
            return null;
        }
        if (url.protocol === "https:" || url.protocol === "http:") return { kind: "link", href: url.href };
        if (STORAGE_SCHEMES.has(url.protocol)) return { kind: "key", key: trimmed };
        // `javascript:`, `data:` and the rest: not something a panel opens.
        return null;
    }

    return { kind: "key", key: trimmed.replace(/^\/+/, "") };
}
